import type { MonthDetailResponse } from '@/services/bankRecon'

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const EMPTY_BUCKETS = {
  matched_cleared: [],
  matched_uncleared: [],
  statement_only: [],
  qbo_only_in_window: [],
  qbo_outstanding: [],
  qbo_stale: [],
  qbo_unreconciled_out_of_window: [],
  overridden: [],
}

export function emptyBankReconMonthDetail(
  accountKey: string,
  reportMonth: string,
): MonthDetailResponse {
  return {
    account_key: accountKey,
    report_month: reportMonth,
    statement: null,
    headlines: { exception_count: 0 },
    buckets: { ...EMPTY_BUCKETS },
    qbo_status_available: false,
  }
}

export function parseBankReconMoney(value: string | number | null | undefined): number {
  if (value == null) return 0
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  const trimmed = value.trim()
  if (!trimmed) return 0
  const normalized = trimmed.replace(/[$,\s]/g, '')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export function formatBankReconMoney(value: string | number | null | undefined): string {
  if (value == null || (typeof value === 'string' && !value.trim())) {
    return '—'
  }
  const num = typeof value === 'number' ? value : parseBankReconMoney(value)
  if (!Number.isFinite(num)) return '—'
  return currencyFmt.format(num)
}

/** Accounting-style amount for table cells: negatives as ($1,234.56). */
export function formatBankReconTableAmount(value: string | number | null | undefined): string {
  if (value == null || (typeof value === 'string' && !value.trim())) {
    return '—'
  }
  const num = typeof value === 'number' ? value : parseBankReconMoney(value)
  if (!Number.isFinite(num)) return '—'
  if (num < 0) {
    return `(${currencyFmt.format(Math.abs(num))})`
  }
  return currencyFmt.format(num)
}

export function isBankReconPdfFilename(filename: string): boolean {
  return filename.trim().toLowerCase().endsWith('.pdf')
}

const BANK_RECON_STATEMENT_FILENAME_RE = /^(\d{6})_(\d{4})\.pdf$/i

export function parseBankReconStatementFilename(
  filename: string,
): { yyyymm: string; accountDigits: string } | null {
  const match = BANK_RECON_STATEMENT_FILENAME_RE.exec(filename.trim())
  if (!match) return null
  const yyyymm = match[1]
  const accountDigits = match[2]
  if (!yyyymm || !accountDigits) return null
  return { yyyymm, accountDigits }
}

export function compactBankReconMonth(reportMonth: string): string {
  return reportMonth.replaceAll('-', '')
}

export function bankReconStatementFilename(reportMonth: string, accountKey: string): string {
  return `${compactBankReconMonth(reportMonth)}_${accountKey}.pdf`
}

export function isBankReconMissingUploadError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const status = (error as { response?: { status?: number } }).response?.status
    if (status === 404) return true
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
    if (typeof detail === 'string' && detail.includes('No bank recon upload found')) {
      return true
    }
  }
  return false
}

export function resolveBankReconUploadAccountKey(
  filename: string,
  accountKeys: string[],
): string | null {
  const parsed = parseBankReconStatementFilename(filename)
  if (!parsed) return null

  const accountDigits = parsed.accountDigits.toLowerCase()
  const sortedKeys = [...accountKeys].sort((a, b) => b.length - a.length)
  for (const key of sortedKeys) {
    if (key.toLowerCase() === accountDigits) {
      return key
    }
  }
  return null
}

export type BankReconBulkUploadPlan = {
  uploads: Map<string, File>
  unmatched: string[]
  duplicateAccounts: string[]
}

export function planBankReconBulkUploads(
  files: ArrayLike<File>,
  accountKeys: string[],
  reportMonth?: string,
): BankReconBulkUploadPlan {
  const uploads = new Map<string, File>()
  const unmatched: string[] = []
  const duplicateAccounts: string[] = []

  for (const file of Array.from(files)) {
    if (!isBankReconPdfFilename(file.name)) {
      unmatched.push(`${file.name} (not a PDF)`)
      continue
    }
    const parsed = parseBankReconStatementFilename(file.name)
    if (!parsed) {
      unmatched.push(`${file.name} (expected YYYYMM_####.pdf)`)
      continue
    }
    if (reportMonth && parsed.yyyymm !== compactBankReconMonth(reportMonth)) {
      unmatched.push(`${file.name} (month ${parsed.yyyymm} does not match ${reportMonth})`)
      continue
    }
    const accountKey = resolveBankReconUploadAccountKey(file.name, accountKeys)
    if (!accountKey) {
      unmatched.push(`${file.name} (no matching account key)`)
      continue
    }
    if (uploads.has(accountKey)) {
      duplicateAccounts.push(accountKey)
    }
    uploads.set(accountKey, file)
  }

  return { uploads, unmatched, duplicateAccounts }
}

export function pickSingleBankReconUpload(
  files: ArrayLike<File>,
): { file: File } | { error: string } {
  const list = Array.from(files)
  if (list.length === 0) {
    return { error: 'Drop a bank statement PDF.' }
  }
  if (list.length > 1) {
    return { error: 'Drop one PDF file at a time.' }
  }
  const file = list[0]
  if (!file || !isBankReconPdfFilename(file.name)) {
    return { error: 'Upload must be a .pdf file.' }
  }
  return { file }
}
